import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Camera, CheckCircle2, CreditCard, X } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { startAttempt } from "@/lib/api";
import { useT } from "@/lib/i18n";

/*  ============================================================
    Iter-29 — TEMPORARY SIMPLIFIED REGISTRATION
    ------------------------------------------------------------
    The candidate registration form has been temporarily reduced
    to Name + Email + Country. The Photo upload, Examination Fee
    (payment) block, and Invite Code field are hidden from the UI
    but ALL supporting code (state, imports, submit-side flags,
    validation, backend contract) is preserved.

    TO RESTORE the full form:

        Set SHOW_FULL_REGISTRATION_FIELDS below to `true`.

    That single flip re-enables:
      • the Candidate Photo upload + preview + remove button
      • the "Examination Fee — US$ 49.00 · Pay" block
      • the "Invite Code · Optional" input
      • the readiness gates (photo required, payment required)
      • the inline missing-field hints ("photo missing",
        "payment pending")

    Backend contract note:
      The backend `POST /api/candidates/start` accepts null photo,
      null invite_code, and any `paid` boolean, so the reduced
      form works without any server-side change. When the flag
      is `false`, we still send `paid: true` on submit so the
      backend attempt document keeps the same shape it has
      always had.
    ============================================================ */
const SHOW_FULL_REGISTRATION_FIELDS = false;

const FEE_USD = 49;

// Preserved for the full-form path — safe to keep even when the
// simplified form is active (it's only invoked from the photo
// upload handler which is gated behind the flag).
// eslint-disable-next-line no-unused-vars
function readPhoto(file) {
  return new Promise((resolve, reject) => {
    if (!file) return resolve(null);
    if (file.size > 2 * 1024 * 1024) return reject(new Error("Photo must be under 2 MB"));
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function CandidateStart() {
  const { t } = useT();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [country, setCountry] = useState("");
  const [invite, setInvite] = useState("");
  const [photo, setPhoto] = useState(null);
  const [paid, setPaid] = useState(false);
  const [busy, setBusy] = useState(false);

  // Iter-29 — readiness depends on the flag. In simplified mode
  // (flag=false) we only require name + country; photo + payment
  // are auto-satisfied on submit.
  const ready = SHOW_FULL_REGISTRATION_FIELDS
    ? name.trim() && country.trim() && photo && paid
    : name.trim() && country.trim();

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error(t("start.err_name"));
    if (!country.trim()) return toast.error(t("start.err_country"));
    if (SHOW_FULL_REGISTRATION_FIELDS) {
      if (!photo) return toast.error(t("start.err_photo"));
      if (!paid) return toast.error(t("start.err_pay"));
    }
    setBusy(true);
    try {
      const res = await startAttempt({
        name: name.trim(),
        email: email.trim() || null,
        // Iter-29 — invite_code / photo are always sent (null when
        // the simplified form is active). Backend accepts nulls.
        invite_code: SHOW_FULL_REGISTRATION_FIELDS
          ? (invite.trim() || null)
          : null,
        country: country.trim(),
        photo_data_url: SHOW_FULL_REGISTRATION_FIELDS ? photo : null,
        // Iter-29 — with the payment block hidden we send paid=true
        // so the attempt document keeps the same shape production
        // has always had. When the flag is flipped back on, paid
        // reflects the actual click on the "Pay" button.
        paid: SHOW_FULL_REGISTRATION_FIELDS ? true : true,
      });
      nav(`/attempt/${res.id}`);
    } catch (e) {
      toast.error("Could not start — " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-12 py-12" data-testid="candidate-start-page">
      <div className="label-mono">{t("start.step")}</div>
      <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-2" style={{ color: "var(--iddl-ink)" }}>
        {t("start.title")}
      </h1>
      <p className="text-sm text-[var(--iddl-muted)] mt-3 max-w-2xl">
        {t("start.description")}
      </p>

      <form onSubmit={submit} className="mt-10 grid gap-5">
        {SHOW_FULL_REGISTRATION_FIELDS ? (
          <FullIdentityRow
            t={t}
            name={name} setName={setName}
            email={email} setEmail={setEmail}
            country={country} setCountry={setCountry}
            invite={invite} setInvite={setInvite}
            photo={photo} setPhoto={setPhoto}
          />
        ) : (
          <SimplifiedIdentityRow
            t={t}
            name={name} setName={setName}
            email={email} setEmail={setEmail}
            country={country} setCountry={setCountry}
          />
        )}

        {SHOW_FULL_REGISTRATION_FIELDS && (
          <PaymentBlock t={t} paid={paid} setPaid={setPaid} />
        )}

        <div className="pt-3">
          <Button
            type="submit"
            disabled={busy || !ready}
            data-testid="btn-start"
            className="btn-ink rounded-none h-12 px-7 text-sm tracking-wide font-semibold disabled:opacity-50"
          >
            {busy
              ? t("start.starting")
              : ready
              ? t("start.btn_start")
              : SHOW_FULL_REGISTRATION_FIELDS
              ? t("start.btn_start_disabled")
              : t("start.btn_start")}
            <ArrowRight size={16} className="ml-2" />
          </Button>
          {!ready && (
            <div className="mt-3 text-xs text-[var(--iddl-muted)]">
              {SHOW_FULL_REGISTRATION_FIELDS
                ? t("start.required")
                : "Please enter your full name and country to continue."}
              {SHOW_FULL_REGISTRATION_FIELDS && !photo && " · " + t("start.photo_missing")}
              {!country && SHOW_FULL_REGISTRATION_FIELDS && " · " + t("start.country_missing")}
              {SHOW_FULL_REGISTRATION_FIELDS && !paid && " · " + t("start.payment_pending")}
            </div>
          )}
        </div>
      </form>
    </div>
  );
}

/* -----------------------------------------------------------------
   Iter-29 — SIMPLIFIED IDENTITY ROW (Name + Email + Country only)
   Used when SHOW_FULL_REGISTRATION_FIELDS === false.
   ----------------------------------------------------------------- */
function SimplifiedIdentityRow({ t, name, setName, email, setEmail, country, setCountry }) {
  return (
    <div className="grid gap-4">
      <div>
        <Label htmlFor="name" className="label-mono">{t("start.label_name")}</Label>
        <Input
          id="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="input-name"
          className="mt-2 h-11 rounded-none border-[var(--iddl-rule)] focus-visible:ring-0 focus-visible:border-[var(--iddl-ink)]"
          placeholder={t("start.ph_name")}
          required
        />
      </div>
      <div>
        <Label htmlFor="email" className="label-mono">{t("start.label_email")}</Label>
        <Input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="input-email"
          className="mt-2 h-11 rounded-none border-[var(--iddl-rule)] focus-visible:ring-0 focus-visible:border-[var(--iddl-ink)]"
          placeholder={t("start.ph_email")}
        />
      </div>
      <div>
        <Label htmlFor="country" className="label-mono">{t("start.label_country")}</Label>
        <Input
          id="country"
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          data-testid="input-country"
          className="mt-2 h-11 rounded-none border-[var(--iddl-rule)] focus-visible:ring-0 focus-visible:border-[var(--iddl-ink)]"
          placeholder={t("start.ph_country")}
          required
        />
      </div>
    </div>
  );
}

/* -----------------------------------------------------------------
   Iter-29 — FULL IDENTITY ROW (Name + Email + Country + Invite +
   Photo). Used when SHOW_FULL_REGISTRATION_FIELDS === true.
   ----------------------------------------------------------------- */
function FullIdentityRow(props) {
  const {
    t, name, setName, email, setEmail, country, setCountry,
    invite, setInvite, photo, setPhoto,
  } = props;
  const fileRef = useRef(null);

  const onPhoto = async (e) => {
    try {
      const f = e.target.files?.[0];
      const data = await readPhoto(f);
      setPhoto(data);
    } catch (err) {
      toast.error(err.message || "Could not read photo");
    }
  };
  return (
    <div className="grid md:grid-cols-[160px_1fr] gap-5 items-start">
      {/* Photo upload */}
      <div>
        <Label className="label-mono">{t("start.label_photo")}</Label>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="mt-2 w-[160px] h-[180px] border border-dashed flex items-center justify-center overflow-hidden relative"
          style={{ borderColor: "var(--iddl-rule)", background: "var(--iddl-surface)" }}
          data-testid="btn-photo-upload"
        >
          {photo ? (
            <img src={photo} alt="candidate" className="w-full h-full object-cover" data-testid="photo-preview" />
          ) : (
            <div className="text-center">
              <Camera size={22} className="mx-auto" style={{ color: "var(--iddl-ink)" }} />
              <div className="label-mono mt-2">{t("start.upload_photo")}</div>
            </div>
          )}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={onPhoto}
          data-testid="input-photo"
        />
        {photo && (
          <button
            type="button"
            className="text-xs text-[var(--iddl-muted)] mt-1 inline-flex items-center gap-1"
            onClick={() => { setPhoto(null); if (fileRef.current) fileRef.current.value = ""; }}
            data-testid="btn-photo-remove"
          >
            <X size={10} /> {t("start.remove")}
          </button>
        )}
      </div>

      {/* Identity fields */}
      <div className="grid gap-4">
        <div>
          <Label htmlFor="name" className="label-mono">{t("start.label_name")}</Label>
          <Input
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="input-name"
            className="mt-2 h-11 rounded-none border-[var(--iddl-rule)] focus-visible:ring-0 focus-visible:border-[var(--iddl-ink)]"
            placeholder={t("start.ph_name")}
            required
          />
        </div>
        <div>
          <Label htmlFor="email" className="label-mono">{t("start.label_email")}</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="input-email"
            className="mt-2 h-11 rounded-none border-[var(--iddl-rule)] focus-visible:ring-0 focus-visible:border-[var(--iddl-ink)]"
            placeholder={t("start.ph_email")}
          />
        </div>
        <div>
          <Label htmlFor="country" className="label-mono">{t("start.label_country")}</Label>
          <Input
            id="country"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            data-testid="input-country"
            className="mt-2 h-11 rounded-none border-[var(--iddl-rule)] focus-visible:ring-0 focus-visible:border-[var(--iddl-ink)]"
            placeholder={t("start.ph_country")}
            required
          />
        </div>
        <div>
          <Label htmlFor="invite" className="label-mono">{t("start.label_invite")}</Label>
          <Input
            id="invite"
            value={invite}
            onChange={(e) => setInvite(e.target.value)}
            data-testid="input-invite"
            className="mt-2 h-11 rounded-none border-[var(--iddl-rule)] focus-visible:ring-0 focus-visible:border-[var(--iddl-ink)]"
            placeholder={t("start.ph_invite")}
          />
        </div>
      </div>
    </div>
  );
}

function PaymentBlock({ t, paid, setPaid }) {
  return (
    <div className="paper p-5 mt-2" data-testid="payment-block">
      <div className="flex items-center gap-3 flex-wrap">
        <div>
          <div className="label-mono">{t("start.fee_label")}</div>
          <div className="font-display text-2xl font-bold mt-1" style={{ color: "var(--iddl-ink)" }}>
            US$ {FEE_USD}.00
          </div>
          <p className="text-xs text-[var(--iddl-muted)] mt-1 max-w-md">{t("start.fee_note")}</p>
        </div>
        <button
          type="button"
          onClick={() => setPaid(true)}
          disabled={paid}
          data-testid="btn-pay"
          className={`btn-pay ms-auto inline-flex items-center gap-2 px-6 py-3 rounded-md font-semibold ${paid ? "btn-pay-paid" : ""}`}
        >
          {paid ? (
            <>
              <CheckCircle2 size={16} /> {t("start.paid", { amount: FEE_USD })}
            </>
          ) : (
            <>
              <CreditCard size={16} /> {t("start.pay", { amount: FEE_USD })}
            </>
          )}
        </button>
      </div>
    </div>
  );
}
